import axios from 'axios';

const API_BASE_URL = '/api/blog-new';
const MEDIA_API_BASE_URL = '/api/blog-media';

// Create axios instance with default config
const blogAPI = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Create media API instance
const mediaAPI = axios.create({
    baseURL: MEDIA_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token and tenant hostname to requests
const requestInterceptor = (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant hostname (required for multi-tenancy)
    const hostname = window.location.hostname;
    config.headers['x-tenant-host'] = hostname;

    return config;
};

blogAPI.interceptors.request.use(requestInterceptor);
mediaAPI.interceptors.request.use(requestInterceptor);

// ==================== Blog Post APIs ====================
export const getAllPosts = (params = {}) => {
    return blogAPI.get('/posts', { params });
};

export const getPostBySlug = (slug) => {
    return blogAPI.get(`/posts/${slug}`);
};

export const createPost = (postData) => {
    return blogAPI.post('/posts', postData);
};

export const updatePost = (id, postData) => {
    return blogAPI.put(`/posts/${id}`, postData);
};

export const deletePost = (id) => {
    return blogAPI.delete(`/posts/${id}`);
};

// ==================== Category APIs ====================
export const getAllCategories = () => {
    return blogAPI.get('/categories');
};

export const createCategory = (categoryData) => {
    return blogAPI.post('/categories', categoryData);
};

export const deleteCategory = (id) => {
    return blogAPI.delete(`/categories/${id}`);
};

// ==================== AI Content Generation APIs ====================
export const generateContent = (data) => {
    // data can contain: prompt, options, skipUsageTracking
    return blogAPI.post('/ai/generate-content', data);
};

export const improveContent = (data) => {
    // data can contain: content, improvementType, skipUsageTracking
    return blogAPI.post('/ai/improve-content', data);
};

export const generateSEO = (data) => {
    // data can contain: title, content, keywords, skipUsageTracking
    return blogAPI.post('/ai/generate-seo', data);
};

export const generateTitles = (data) => {
    // data can contain: content, topic, keywords, count, skipUsageTracking
    return blogAPI.post('/ai/generate-titles', data);
};

export const generateImage = (data) => {
    // data can contain: prompt, size, style, options, skipUsageTracking
    return blogAPI.post('/ai/generate-image', data);
};

export const generateExcerpt = (data) => {
    // data can contain: content, maxLength, skipUsageTracking
    return blogAPI.post('/ai/generate-excerpt', data);
};

export const generateTags = (data) => {
    // data can contain: title, content, count, skipUsageTracking
    return blogAPI.post('/ai/generate-tags', data);
};

export const analyzePost = (data) => {
    // data can contain: title, content, excerpt, slug, tags, seo
    return blogAPI.post('/ai/analyze-post', data);
};

// ==================== AI Podcast & Video APIs ====================
export const generatePodcast = (postId, options) => {
    return blogAPI.post(`/posts/${postId}/generate-podcast`, options);
};

export const generateVideo = (postId, options) => {
    return blogAPI.post(`/posts/${postId}/generate-video`, options);
};

export const togglePodcast = (postId, isEnabled) => {
    return blogAPI.patch(`/posts/${postId}/toggle-podcast`, { isEnabled });
};

export const toggleVideo = (postId, isEnabled) => {
    return blogAPI.patch(`/posts/${postId}/toggle-video`, { isEnabled });
};

export const checkVideoStatus = (videoId) => {
    return blogAPI.get(`/video-status/${videoId}`);
};

// ==================== Subscription APIs ====================
export const getSubscriptionPlans = (type = 'blog') => {
    return blogAPI.get('/subscription-plans', { params: { type } });
};

export const getUserSubscription = (type = 'blog') => {
    return blogAPI.get('/subscription', { params: { type } });
};

export const createSubscription = (planId, paymentData = {}) => {
    return blogAPI.post('/subscription', { planId, paymentData });
};

export const getUsageStats = () => {
    return blogAPI.get('/usage-stats');
};

// Record AI usage manually (for when skipUsageTracking was used)
export const recordAIUsage = (data) => {
    // data can contain: action, quantity, data (metadata)
    return blogAPI.post('/usage/record', data);
};

// Check quota before performing AI action
export const checkQuota = (action, quantity = 1) => {
    // action: 'post', 'image', 'content', 'scheduledPost', 'analysis', etc.
    // quantity: number of items to check
    return blogAPI.post('/usage/check-quota', { action, quantity });
};

// ==================== Media Library APIs ====================
export const uploadMedia = (formData) => {
    return mediaAPI.post('/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const getAllMedia = (params = {}) => {
    return mediaAPI.get('/', { params });
};

export const getMediaById = (id) => {
    return mediaAPI.get(`/${id}`);
};

export const updateMedia = (id, mediaData) => {
    return mediaAPI.put(`/${id}`, mediaData);
};

export const deleteMedia = (id) => {
    return mediaAPI.delete(`/${id}`);
};

export const getMediaFolders = () => {
    return mediaAPI.get('/folders/list');
};

export const getStorageStats = () => {
    return mediaAPI.get('/stats/storage');
};

// Default export with all methods
export default {
    // Posts
    getAllPosts,
    getPostBySlug,
    createPost,
    updatePost,
    deletePost,

    // Categories
    getAllCategories,
    createCategory,
    deleteCategory,

    // AI Content Generation
    generateContent,
    improveContent,
    generateSEO,
    generateTitles,
    generateImage,
    generateExcerpt,
    generateTags,
    analyzePost,

    // AI Podcast & Video
    generatePodcast,
    generateVideo,
    togglePodcast,
    toggleVideo,
    checkVideoStatus,

    // Subscription
    getSubscriptionPlans,
    getUserSubscription,
    createSubscription,
    getUsageStats,
    recordAIUsage,
    checkQuota,

    // Media Library
    uploadMedia,
    getAllMedia,
    getMediaById,
    updateMedia,
    deleteMedia,
    getMediaFolders,
    getStorageStats,
};

